import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Stepper } from "@/components/Stepper";
import { EmptyState } from "@/components/EmptyState";
import { ErrorState } from "@/components/ErrorState";
import { PraticheSkeleton } from "@/components/PraticheSkeleton";
import { TipoIcon } from "@/components/TipoIcon";
import { getPratica } from "@/api/pratiche";
import { downloadBollettaFile } from "@/api/bollette";
import { ApiError } from "@/api/client";
import { ArrowLeft, FileText, Search, Download } from "lucide-react";
import { formatDateTime } from "@/lib/dates";
import type { Pratica } from "@/types";

export function DettaglioPratica() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [pratica, setPratica] = useState<Pratica | null>(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!id) return;
    setLoading(true);
    setNotFound(false);
    setError(null);
    getPratica(id)
      .then(setPratica)
      .catch((e) => {
        if (e instanceof ApiError && e.status === 404) {
          setNotFound(true);
        } else if (e instanceof ApiError && e.status >= 500) {
          setError("Errore del sistema CRM. Riprova tra qualche istante.");
        } else {
          setError("Impossibile caricare la pratica.");
        }
      })
      .finally(() => setLoading(false));
  }, [id]);

  if (loading) return <PraticheSkeleton />;
  if (notFound) {
    return (
      <div className="space-y-4">
        <button
          onClick={() => navigate("/pratiche")}
          className="flex items-center gap-2 text-sm text-muted-foreground hover:text-text"
        >
          <ArrowLeft size={16} /> Torna alle pratiche
        </button>
        <EmptyState icon={<Search size={26} className="text-brand" />} title="Pratica non trovata" />
      </div>
    );
  }
  if (error) return <ErrorState message={error} />;
  if (!pratica) return null;

  return (
    <div className="space-y-4">
      <button
        onClick={() => navigate("/pratiche")}
        className="flex items-center gap-2 text-sm text-muted-foreground hover:text-text"
      >
        <ArrowLeft size={16} /> Torna alle pratiche
      </button>
      <Stepper currentStage={pratica.stage} />
      <div className="grid grid-cols-2 gap-4">
        <div className="bg-card p-4 rounded-lg border border-border">
          <h3 className="font-semibold text-text mb-3">Dati pratica</h3>
          <div className="space-y-2 text-sm">
            <div><span className="text-muted-foreground">Da:</span> <span className="text-text">{pratica.fornitoreAttuale}</span></div>
            <div><span className="text-muted-foreground">A:</span> <span className="text-text">{pratica.fornitoreNuovo}</span></div>
            <div><span className="text-muted-foreground">Tipo:</span> <span className="text-text">{pratica.tipo}</span></div>
            <div><span className="text-muted-foreground">Data richiesta:</span> <span className="text-text">{formatDateTime(pratica.dataCreazione)}</span></div>
          </div>
        </div>
        {pratica.offerta && (
        <div className="bg-card p-4 rounded-lg border border-border">
          <h3 className="font-semibold text-text mb-3">Offerta scelta</h3>
          <div className="space-y-2 text-sm">
            <div><span className="text-muted-foreground">Offerta:</span> <span className="text-text">{pratica.offerta}</span></div>
            <div><span className="text-muted-foreground">Fornitore:</span> <span className="text-text">{pratica.fornitoreNuovo}</span></div>
            <div><span className="text-muted-foreground">Prezzo:</span> <span className="text-text">{pratica.prezzoUnitario} {pratica.unitaPrezzo}</span></div>
            <div><span className="text-muted-foreground">Quota fissa:</span> <span className="text-text">€{pratica.quotaFissaMese}/mese</span></div>
            <div><span className="text-muted-foreground">Durata:</span> <span className="text-text">{pratica.durataMesi} mesi</span></div>
            <div className="text-brand font-semibold">Risparmio stimato: €{pratica.risparmioAnnuo}/anno</div>
          </div>
        </div>
        )}
      </div>
      <div className="bg-card p-4 rounded-lg border border-border">
        <h3 className="font-semibold text-text mb-3">Documenti</h3>
        {pratica.documenti.length === 0 ? (
          <p className="text-sm text-muted-foreground">Nessun documento allegato.</p>
        ) : (
          <div className="flex gap-2">
            {pratica.documenti.map((doc) => (
              <div key={doc.id} className="flex items-center gap-2 bg-surface px-3 py-2 rounded-lg border border-border text-sm">
                <FileText size={14} className="text-muted-foreground" /> {doc.nome}
              </div>
            ))}
          </div>
        )}
      </div>
      {pratica.bollette && pratica.bollette.length > 0 && (
        <div className="bg-card p-4 rounded-lg border border-border">
          <h3 className="font-semibold text-text mb-1">Bollette collegate</h3>
          <p className="text-xs text-muted-foreground mb-3">
            Le bollette da cui è nata questa pratica
          </p>
          <div className="space-y-2">
            {pratica.bollette.map((b) => (
              <div
                key={b.id}
                className="flex items-center justify-between py-2 border-t border-border first:border-t-0"
              >
                <div className="flex items-center gap-3">
                  <span className="text-lg flex items-center gap-1">
                    <TipoIcon tipo={b.tipoFornitura} />
                  </span>
                  <div>
                    <div className="font-medium text-text">{b.name}</div>
                    <div className="text-xs text-muted-foreground">
                      {formatDateTime(b.dataBolletta)} · € {b.importoMensile.toFixed(2)}
                    </div>
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  {b.hasFile && (
                    <button
                      onClick={() => downloadBollettaFile(b.id, `${b.name}.pdf`).catch(() => {})}
                      className="text-sm text-brand hover:underline inline-flex items-center gap-1"
                    >
                      <Download size={14} /> Scarica
                    </button>
                  )}
                  <button
                    onClick={() => navigate(`/bollette/${b.id}`)}
                    className="text-sm text-brand hover:underline"
                  >
                    Dettaglio →
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
